import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Spin, Switch } from "antd";
import Card from "../../Card";
import Button from "../../Button";
import TextField from "../../TextField";
import Table from "../../Table";
import { useMasterDataApi } from "../../../hooks/useMasterDataApi";
import { Icon } from "@iconify/react";

const normalizeField = (field, idx) => {
  const id =
    field?.id ??
    field?.dropdown_id ??
    field?.uuid ??
    field?.key ??
    field?.field ??
    idx;

  const key = field?.key ?? field?.field ?? field?.code ?? field?.name ?? "";
  const label = field?.label ?? field?.title ?? field?.displayName ?? key ?? "";
  const isActive =
    field?.is_active ?? field?.isActive ?? field?.active ?? field?.enabled;

  return {
    ...field,
    id,
    key,
    label,
    isActive,
  };
};

const isValidKey = (value) => /^[a-zA-Z][a-zA-Z0-9_]*$/.test(value);

const DropdownManager = ({ toast }) => {
  const {
    normalizeError,
    listDropdownFields,
    createDropdownField,
    updateDropdownField,
    deleteDropdownField,
  } = useMasterDataApi();

  const [fields, setFields] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);

  const [fieldKey, setFieldKey] = useState("");
  const [fieldLabel, setFieldLabel] = useState("");
  const [errors, setErrors] = useState({ key: "", label: "" });

  const normalizedFields = useMemo(
    () => fields.map((f, idx) => normalizeField(f, idx)),
    [fields]
  );

  const fieldKeySet = useMemo(() => {
    const set = new Set();
    normalizedFields.forEach((f) => {
      if (f?.key) set.add(String(f.key).trim().toLowerCase());
    });
    return set;
  }, [normalizedFields]);

  const loadFields = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await listDropdownFields(includeInactive);
      setFields(Array.isArray(data) ? data : []);
    } catch (err) {
      toast?.error(normalizeError(err));
      setFields([]);
    } finally {
      setListLoading(false);
    }
  }, [listDropdownFields, includeInactive, normalizeError, toast]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const openEdit = (field) => {
    setEditingField(field);
    setEditLabel(field?.label ?? "");
    setEditActive(field?.isActive !== false);
  };

  const handleUpdateField = async (e) => {
    e.preventDefault();
    if (!editingField?.key) return;
    setUpdateLoading(true);
    try {
      await updateDropdownField(editingField.key, {
        label: editLabel.trim(),
        is_active: editActive,
      });
      toast?.success("Dropdown field updated.");
      setEditingField(null);
      await loadFields();
    } catch (err) {
      toast?.error(normalizeError(err));
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteField = async (field) => {
    if (!field?.key) return;
    if (!window.confirm(`Delete dropdown field "${field.label || field.key}"? This may affect options.`)) return;
    setDeletingKey(field.key);
    try {
      await deleteDropdownField(field.key);
      toast?.success("Dropdown field deleted.");
      setEditingField(null);
      await loadFields();
    } catch (err) {
      toast?.error(normalizeError(err));
    } finally {
      setDeletingKey(null);
    }
  };

  const validate = () => {
    const key = fieldKey.trim();
    const label = fieldLabel.trim();

    const next = { key: "", label: "" };
    if (!key) next.key = "Key is required.";
    else if (!isValidKey(key)) {
      next.key = "Key must start with a letter and contain only letters, numbers, and underscores.";
    } else if (fieldKeySet.has(key.toLowerCase())) {
      next.key = "This key already exists.";
    }

    if (!label) next.label = "Label is required.";
    else if (label.length > 120) next.label = "Label must be 120 characters or less.";

    setErrors(next);
    return !next.key && !next.label;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setCreateLoading(true);
    try {
      await createDropdownField({ key: fieldKey.trim(), label: fieldLabel.trim() });
      toast?.success("Dropdown field created successfully.");
      setFieldKey("");
      setFieldLabel("");
      setErrors({ key: "", label: "" });
      await loadFields();
    } catch (err) {
      toast?.error(normalizeError(err));
    } finally {
      setCreateLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { title: "Key", dataIndex: "key", key: "key" },
      { title: "Label", dataIndex: "label", key: "label" },
      {
        title: "Active",
        dataIndex: "isActive",
        key: "isActive",
        render: (value) => {
          if (value === undefined || value === null) return "-";
          return Boolean(value) ? "Yes" : "No";
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <div className="flex items-center gap-2">
            <Button
              size="small"
              variant="secondary"
              onClick={() => openEdit(record)}
              startIcon={<Icon icon="lucide:edit" className="h-3.5 w-3.5" />}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="secondary"
              onClick={() => handleDeleteField(record)}
              disabled={deletingKey === record.key}
              startIcon={<Icon icon="lucide:trash-2" className="h-3.5 w-3.5" />}
            >
              {deletingKey === record.key ? "..." : "Delete"}
            </Button>
          </div>
        ),
      },
    ],
    [deletingKey]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card header="Create new dropdown field">
        <form className="space-y-4" onSubmit={handleCreate}>
          <TextField
            label="Field Key"
            name="fieldKey"
            value={fieldKey}
            onChange={(e) => setFieldKey(e.target.value.replace(/\s+/g, "_"))}
            placeholder="e.g., industry, skill_level"
            required
            error={Boolean(errors.key)}
            helperText={errors.key || "Letters/numbers/underscore only. Used in APIs."}
          />

          <TextField
            label="Field Label"
            name="fieldLabel"
            value={fieldLabel}
            onChange={(e) => setFieldLabel(e.target.value)}
            placeholder="e.g., Industry, Skill Level"
            required
            error={Boolean(errors.label)}
            helperText={errors.label || "Human-friendly label shown in the UI."}
          />

          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="submit" variant="primary" loading={createLoading}>
              Create Field
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setFieldKey("");
                setFieldLabel("");
                setErrors({ key: "", label: "" });
              }}
              disabled={createLoading}
            >
              Clear
            </Button>
          </div>
        </form>
      </Card>

      <Card
        header={
          <div className="flex items-center justify-between w-full flex-wrap gap-2">
            <span>Existing dropdown fields</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Include inactive</span>
              <Switch
                checked={includeInactive}
                onChange={setIncludeInactive}
              />
              <Button
                size="small"
                variant="secondary"
                onClick={loadFields}
                disabled={listLoading}
              >
                Refresh
              </Button>
            </div>
          </div>
        }
      >
        <Spin spinning={listLoading}>
          <Table
            columns={columns}
            data={normalizedFields}
            rowKey="id"
            emptyMessage="No dropdown fields found."
          />
        </Spin>
      </Card>

      <Modal
        title="Edit dropdown field"
        open={Boolean(editingField)}
        onCancel={() => setEditingField(null)}
        footer={null}
        destroyOnClose
      >
        {editingField && (
          <form className="space-y-4" onSubmit={handleUpdateField}>
            <TextField
              label="Key"
              name="editKey"
              value={editingField.key}
              disabled
              helperText="Key cannot be changed."
            />
            <TextField
              label="Label"
              name="editLabel"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              placeholder="e.g., Education Level"
              required
            />
            <div className="flex items-center gap-2">
              <Switch
                checked={editActive}
                onChange={setEditActive}
              />
              <span className="text-sm text-slate-700">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEditingField(null)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={updateLoading}>
                Save
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default DropdownManager;

